import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, formatDistanceToNow, isToday, isYesterday, isThisWeek, getYear, differenceInMinutes } from "date-fns";
import { ar, enUS } from "date-fns/locale"; // Import the locales you need

/**
 * Hook to format timestamps into readable date strings based on language settings.
 *
 * @param {number} timestamp - The timestamp to format.
 * @returns {string} - Formatted and localized date string.
 */
export const useFormattedTimestamp = (timestamp: number): string => {
  const { t, i18n } = useTranslation();
  const [locale, setLocale] = useState(enUS); // Default locale

  useEffect(() => {
    // Set the locale based on the current language
    setLocale(i18n.language === "ar" ? ar : enUS);
  }, [i18n.language]);

  const date = new Date(timestamp);
  const now = new Date();

  // Localized time formatting based on language
  const timeAgo = formatDistanceToNow(date, {
    addSuffix: true,
    locale,
  });

  if (isToday(date)) {
    return timeAgo === "less than a minute ago" ? t("timestamp.justNow") : t("timestamp.timeAgo", { timeAgo });
  } else if (isYesterday(date)) {
    return t("timestamp.yesterday", { time: format(date, "hh:mm a", { locale }) });
  } else if (isThisWeek(date)) {
    return t("timestamp.thisWeek", { day: format(date, "EEEE", { locale }) });
  } else if (getYear(date) === getYear(now) - 1) {
    return t("timestamp.lastYear", { date: format(date, "do MMMM", { locale }) });
  } else {
    return t("timestamp.other", { date: format(date, "do MMMM yyyy", { locale }) });
  }
};
